/**
 * UserTransactionHistoryPage
 * UC-050: View Personal Transaction History
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { api } from '../core/api-client';
import { useApi } from '../hooks/useApi';
import { TransactionListResponse } from '../types';
import { Button, Input, Card } from '../components/ui';
import { Select } from '../components/ui/Select';
import { DatePicker } from '../components/ui/DatePicker';
import { fmt, fmtMoney } from '../utils/format';

interface PaginatedResponse<T> {
  items: T[];
  total: number;
  skip: number;
  limit: number;
  has_more: boolean;
}

interface TransactionFilters {
  status?: string;
  type?: string;
  from_date?: string;
  to_date?: string;
  date?: string;
}

export function UserTransactionHistoryPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  // State for filters
  const [filters, setFilters] = useState<TransactionFilters>({});
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [fromDateValue, setFromDateValue] = useState<string>('');
  const [toDateValue, setToDateValue] = useState<string>('');

  // Error state
  const [showError, setShowError] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  // Status options — values match the Transaction.status DB enum.
  const statusOptions = [
    { value: '', label: t('userTransactionHistory.filters.allStatuses', 'All statuses') },
    { value: 'Succeeded', label: t('userTransactionHistory.filters.succeeded', 'Succeeded') },
    { value: 'Pending', label: t('userTransactionHistory.filters.pending', 'Pending') },
    { value: 'Failed', label: t('userTransactionHistory.filters.failed', 'Failed') },
  ];

  // API call to get transactions
  const {
    data: transactionsData,
    loading,
    error,
    refetch: refetchTransactions
  } = useApi<PaginatedResponse<TransactionListResponse>>(
    '/transactions',
    {
      params: {
        limit: 20,
        page: 1,
        ...filters
      },
      onError: (error) => {
        setShowError(true);
        setErrorMessage(
          error.message ||
            t(
              'userTransactionHistory.errors.stripeUnavailable',
              'Payment service is temporarily unavailable. Showing cached data.',
            ),
        );
        toast.error(t('userTransactionHistory.errors.loadFailed', 'Failed to load transactions'));
      },
      onSuccess: () => {
        setShowError(false);
      }
    }
  );

  // useApi auto-fetches only once on mount. Re-run when filters change so
  // the list reflects the current status / date-range selection.
  useEffect(() => {
    refetchTransactions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const transactions = transactionsData?.items || [];
  const showEmptyState = !loading && !showError && transactions.length === 0;

  // Filter handlers
  const handleStatusFilter = (status: string) => {
    setStatusFilter(status);
    updateFilters({ status: status || undefined });
  };

  const handleFromDateChange = (date: string) => {
    setFromDateValue(date);
    updateFilters({ from_date: date || undefined });
  };

  const handleToDateChange = (date: string) => {
    setToDateValue(date);
    updateFilters({ to_date: date || undefined });
  };

  const updateFilters = (newFilters: Partial<TransactionFilters>) => {
    const updatedFilters = { ...filters, ...newFilters };
    Object.keys(updatedFilters).forEach((key) => {
      if (updatedFilters[key as keyof TransactionFilters] === undefined) {
        delete updatedFilters[key as keyof TransactionFilters];
      }
    });
    setFilters(updatedFilters);
  };

  const formatDate = (dateStr: string): string => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatAmount = (amount: number): string => {
    return fmtMoney(amount, 2);
  };

  const getStatusColor = (status: string): string => {
    switch (status.toLowerCase()) {
      case 'succeeded':
        return 'bg-green-100 text-green-700 border-green-200';
      case 'pending':
        return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      case 'failed':
        return 'bg-red-100 text-red-700 border-red-200';
      case 'refunded':
        return 'bg-gray-100 text-gray-700 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-600 border-gray-200';
    }
  };

  const handleTransactionClick = (transactionId: number) => {
    navigate(`/user-transaction-detail/${transactionId}`);
  };

  const retryLoad = () => {
    setShowError(false);
    refetchTransactions();
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-full overflow-x-hidden">
      <div className="mb-5 sm:mb-6">
        <h1 className="text-2xl lg:text-3xl font-bold text-gray-900">Transaction History</h1>
        <p className="mt-1.5 text-base sm:text-sm text-gray-600">View your personal transaction records and payment history</p>
      </div>

      {/* Filter Section */}
      <div className="mb-5 sm:mb-6 p-4 bg-[var(--surface-base)] rounded-xl border border-[var(--border-subtle)] overflow-visible">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 overflow-visible">
          <Select<string>
            label={t('userTransactionHistory.filters.statusLabel', 'Status')}
            value={statusFilter}
            onChange={handleStatusFilter}
            options={statusOptions}
          />
          <DatePicker
            label={t('userTransactionHistory.filters.fromDate', 'From date')}
            value={fromDateValue}
            onChange={handleFromDateChange}
            placeholder="DD/MM/YYYY"
            todayLabel={t('userTransactionHistory.filters.today', 'Today')}
            clearLabel={t('userTransactionHistory.filters.clear', 'Clear')}
          />
          <DatePicker
            label={t('userTransactionHistory.filters.toDate', 'To date')}
            value={toDateValue}
            onChange={handleToDateChange}
            placeholder="DD/MM/YYYY"
            todayLabel={t('userTransactionHistory.filters.today', 'Today')}
            clearLabel={t('userTransactionHistory.filters.clear', 'Clear')}
          />
        </div>
      </div>

      {/* Error State */}
      {showError && (
        <div className="mb-5 sm:mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center gap-3">
            <svg className="h-5 w-5 text-red-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            <div>
              <h3 className="text-sm font-medium text-red-800">Unable to load transactions</h3>
              <p className="text-sm text-red-700 mt-1">{errorMessage}</p>
            </div>
          </div>
          <div className="mt-3">
            <Button
              onClick={retryLoad}
              variant="outline"
              size="sm"
              className="text-red-700 border-red-300 hover:bg-red-100"
            >
              Retry
            </Button>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && !showError && (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading transactions...</p>
        </div>
      )}

      {/* Empty State */}
      {showEmptyState && (
        <div className="text-center py-12">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
          </svg>
          <h3 className="mt-4 text-lg font-medium text-gray-900">No transactions found</h3>
          <p className="mt-2 text-base sm:text-sm text-gray-600">
            You haven't made any transactions yet or no transactions match your current filters.
          </p>
          <div className="mt-6">
            <Button
              onClick={() => navigate('/user-dashboard')}
              className="w-full sm:w-auto px-5 py-3 sm:py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-base sm:text-sm font-medium transition-colors"
            >
              Back to Dashboard
            </Button>
          </div>
        </div>
      )}

      {/* Transaction Cards */}
      {!showEmptyState && !loading && !showError && (
        <div className="space-y-4">
          {transactions.map((transaction) => (
            <div
              key={transaction.transactionId}
              onClick={() => handleTransactionClick(transaction.transactionId)}
              className="bg-white rounded-lg border border-gray-200 p-4 cursor-pointer hover:bg-gray-50 hover:border-gray-300 active:scale-[0.99] transition-all"
            >
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-base sm:text-sm font-medium text-gray-900">
                      Transaction {transaction.transactionId}
                    </h3>
                    <span className={`px-2.5 py-1 text-xs font-medium rounded-full border ${getStatusColor(transaction.status)}`}>
                      {transaction.status}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600">{formatDate(transaction.date)}</p>
                  <p className="text-xs text-gray-500 mt-1">{transaction.type}</p>
                </div>
                <div className="flex items-center justify-between sm:justify-end gap-3 sm:flex-shrink-0">
                  <span className="text-lg font-semibold text-gray-900">
                    {formatAmount(transaction.amount)}
                  </span>
                  <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Navigation Actions */}
      <div className="mt-8 flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
        <Button
          onClick={() => navigate('/user-dashboard')}
          variant="outline"
          className="w-full sm:w-auto px-5 py-3 sm:py-2.5 text-base sm:text-sm font-medium"
        >
          Back to Dashboard
        </Button>
      </div>
    </div>
  );
}

export default UserTransactionHistoryPage;
